import React, { useMemo } from "react";
import {
  Autocomplete,
  Box,
  Checkbox,
  TextField,
  Typography,
} from "@mui/material";

const normalize = (value) => String(value || "").trim().toLowerCase();

const defaultGetOptionValue = (option) => String(option?._id || option?.imei || option?.deviceId || "");

const defaultGetOptionLabel = (option) => {
  const name = option?.name || option?.vehicleName || option?.label || "Unnamed";
  const imei = option?.imei || option?.deviceId || option?._id || "-";
  const plate =
    option?.vehicleNumber ||
    option?.plateNumber ||
    option?.plateNo ||
    option?.registrationNo ||
    option?.registrationNumber ||
    "";
  return plate ? `${name} (${imei}) • ${plate}` : `${name} (${imei})`;
};

function buildSearchText(option) {
  return [
    option?.name,
    option?.vehicleName,
    option?.label,
    option?.imei,
    option?.deviceId,
    option?._id,
    option?.vehicleNumber,
    option?.plateNumber,
    option?.plateNo,
    option?.registrationNo,
    option?.registrationNumber,
  ]
    .map(normalize)
    .filter(Boolean)
    .join(" ");
}

export default function SearchableDeviceSelect({
  options = [],
  value,
  onChange,
  label = "Vehicle",
  placeholder = "Search by name, IMEI, or plate",
  disabled = false,
  multiple = false,
  clearable = true,
  size = "small",
  getOptionLabel = defaultGetOptionLabel,
  getOptionValue = defaultGetOptionValue,
  helperText = "",
  sx = {},
  textFieldProps = {},
}) {
  const safeOptions = useMemo(
    () => (Array.isArray(options) ? options.filter(Boolean) : []),
    [options]
  );

  return (
    <Autocomplete
      multiple={multiple}
      options={safeOptions}
      value={value}
      onChange={(_, nextValue) => onChange?.(nextValue)}
      disableCloseOnSelect={multiple}
      disabled={disabled}
      clearOnBlur={false}
      autoHighlight
      fullWidth
      isOptionEqualToValue={(option, selected) =>
        String(getOptionValue(option)) === String(getOptionValue(selected))
      }
      getOptionLabel={getOptionLabel}
      filterOptions={(items, state) => {
        const query = normalize(state.inputValue);
        if (!query) return items;
        return items.filter((item) => buildSearchText(item).includes(query));
      }}
      renderOption={(props, option, { selected }) => (
        <li {...props}>
          {multiple ? <Checkbox sx={{ mr: 1 }} checked={selected} /> : null}
          <Box sx={{ minWidth: 0, py: 0.35 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: "#0f172a",
                whiteSpace: "normal",
                wordBreak: "break-word",
                lineHeight: 1.35,
              }}
            >
              {getOptionLabel(option)}
            </Typography>
          </Box>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          helperText={helperText}
          size={size}
          {...textFieldProps}
        />
      )}
      clearOnEscape={clearable}
      sx={{
        "& .MuiOutlinedInput-root": {
          borderRadius: 2.5,
          minHeight: multiple ? undefined : 48,
          alignItems: multiple ? "flex-start" : "center",
          backgroundColor: "#fff",
        },
        "& .MuiAutocomplete-inputRoot": {
          flexWrap: multiple ? "wrap" : "nowrap",
          pr: 1,
        },
        "& .MuiAutocomplete-input": {
          minWidth: "120px !important",
          textOverflow: "ellipsis",
        },
        "& .MuiAutocomplete-tag": {
          maxWidth: "calc(100% - 10px)",
        },
        "& .MuiFormHelperText-root": {
          marginLeft: 0,
        },
        ...sx,
      }}
    />
  );
}
